<template>
  <b-row>
    <b-col
      lg="10"
      xl="8"
    >
      <div
        class="card"
        style="height: 34rem"
      >
        <div
          class="card-header"
          v-if="accessPublisherSelection"
        >
          <div
            class="card-options"
            data-test="publisher-selection-view-toggle"
          >
            Verticals&nbsp;&nbsp;
            <b-form-checkbox
              v-model="showIndividualPublisherSelection"
              name="check-button"
              switch
            >
              Publishers
            </b-form-checkbox>
          </div>
        </div>
        <div
          class="card-body o-auto"
        >
          <div
            v-if="isLoading"
            class="loader m-auto"
          />
          <ul
            v-else-if="showIndividualPublisherSelection"
            class="list-unstyled list-separated"
            data-test="campaign-publisher-list"
          >
            <template
              v-for="publisher in publishersListAlphabetically"
            >
              <li
                :key="publisher.id"
                class="list-separated-item border-bottom"
                data-test="campaign-publisher-list-item"
              >
                <div class="row align-items-center">
                  <div
                    class="col-auto"
                    data-test="campaign-publisher-checkbox"
                  >
                    <b-form-checkbox
                      :id="`checkbox-${publisher.id}`"
                      v-model.lazy="publisher.status"
                      :name="`checkbox-${publisher.id}`"
                      @change="changePublisherStatus(publisher.id)"
                      size="xl"
                      class="publisher-checkbox"
                      data-test="campaign-publisher-list-item-checkbox"
                      :disabled="isCampaignReadonly"
                    />
                  </div>
                  <div class="col-auto">
                    <BaseCompanyLogo
                      :company="publisher"
                    />
                  </div>
                  <div class="col">
                    <div
                      class="text-inherit"
                      data-test="campaign-publisher-list-item-name"
                    >
                      {{ publisher.name }}
                    </div>
                    <small class="d-block item-except text-sm h-1x">
                      <a
                        :href="`http://${publisher.domain}`"
                        target="_blank"
                      >
                        {{ publisher.domain }}
                      </a>
                    </small>
                  </div>
                  <div
                    class="col-3 text-center"
                    v-if="isManagedCampaign"
                  >
                    <div
                      v-if="publisherHasDeals(publisher.id)"
                    >
                      <div
                        :class="[getPublisherDealType(publisher.id) === 'Preferred' ? 'tag-azure' : 'tag-gray']"
                        class="text-inherit tag text-white"
                        data-test="campaign-publisher-list-item-name"
                      >
                        {{ getPublisherDealType(publisher.id) }}
                      </div>
                      <small
                        :data-test="`${publisher.name}-deal-collapse-button`"
                        class="d-block item-except text-sm text-muted h-1x text-underline"
                        v-b-toggle="`accordion-${publisher.name}-deal`"
                      >
                        <button
                          class="btn btn-sm btn-link small when-closed"
                        >
                          Show deals
                        </button>
                        <button
                          class="btn btn-sm btn-link small when-opened"
                        >
                          Hide deals
                        </button>
                      </small>
                    </div>
                    <small
                      :data-test="`${publisher.name}-creative-collapse-button`"
                      class="d-block item-except text-sm text-muted h-1x text-underline"
                      v-b-toggle="`accordion-${publisher.name}-creative`"
                    >
                      <button
                        class="btn btn-sm btn-link small when-closed"
                      >
                        Show Creatives
                      </button>
                      <button
                        class="btn btn-sm btn-link small when-opened"
                      >
                        Hide Creatives
                      </button>
                    </small>
                  </div>
                </div>
              </li>
              <b-collapse
                :id="`accordion-${publisher.name}-deal`"
                :key="`publisher-deals-${publisher.id}`"
                accordion="my-accordion"
                class="py-4 pl-5 border-bottom"
                role="tabpanel"
                v-if="isManagedCampaign && publisherHasDeals(publisher.id)"
              >
                <table
                  class="table table border mb-0 publisher-deal-table"
                >
                  <tbody
                    class="border-left"
                  >
                    <tr
                      :key="`publisher-deal-${deal.id}`"
                      v-for="deal in publisherDeals[publisher.id].deals"
                    >
                      <td
                        class="text-right small"
                        width="1"
                      >
                        {{ getCreativeSize(deal.creativeSizeId) }}
                      </td>
                      <td class="small">
                        {{ deal.dealId }}
                      </td>
                      <td class="text-right small">
                        <span class="text-muted">
                          ${{ (deal.maxCpmMicros / 1000000).toFixed(2) }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </b-collapse>
              <b-collapse
                v-if="isManagedCampaign"
                class="py-4 pl-5 border-bottom"
                :id="`accordion-${publisher.name}-creative`"
                :key="`publisher-creatives-${publisher.id}`"
                accordion="my-accordion"
                role="tabpanel"
              >
                <table
                  class="table table border mb-0 publisher-deal-table"
                >
                  <tbody
                    class="border-left"
                  >
                    <tr
                      v-for="creative in getActiveCreativeTemplates"
                      :key="`publisher-deal-${creative.id}`"
                    >
                      <td
                        class="text-left small"
                      >
                        {{ creative.name }}
                      </td>
                      <td class="text-right small custom-pub-c-slider">
                        <b-form-checkbox
                          name="check-button"
                          switch
                          :disabled="isCampaignReadonly"
                          :checked="getPublishersCreativesSliderValue(publisher.id, creative.id)"
                          @change="updatePublishersCreativesState(publisher.id, creative.id, $event)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </b-collapse>
            </template>
          </ul>
          <template v-else>
            <GroupPublisherSelection
              :publisher-groups="publisherGroups"
              :publishers="publishersList"
              :selected-publisher-ids.sync="publisherIds"
              :selected-publisher-groups.sync="selectedPublisherGroups"
              :disabled="disabled"
              @togglePublisherStatus="togglePublisherStatus"
              @updateSelectedGroups="updateSelectedGroups"
            />
          </template>
        </div>
      </div>
      <div
        class="d-flex"
      >
        <small class="d-block text-muted">
          Logos provided by
          <a
            href="https://clearbit.com"
            target="_blank"
          >clearbit.com</a>
        </small>
      </div>
    </b-col>

    <div class="col">
      <span
        class="badge badge-default"
        :class="{ 'bg-orange-light': reachedMinimumPublishers }"
      >{{ totalselectedPublishers }} / {{ totalPublishers }} Publishers</span>
      <div class="mt-3">
        <ManageDeals
          v-if="isManagedCampaign"
          class="ml-auto"
          :publishers="publishersList"
          :creative-template-ids="creativeTemplateIds"
        />
      </div>
    </div>
  </b-row>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as types from '@/modules/campaigns/store/types';
import feature from 'vue-feature-toggle';
import helper from '@/core/helper';

import GroupPublisherSelection from '@/modules/campaigns/components/GroupPublisherSelection';
import ManageDeals from '@/modules/campaigns/components/ManageDeals';

export default {
  name: 'CustomPublishers',
  components: {
    GroupPublisherSelection,
    ManageDeals,
  },
  props: {
    orderSet: {
      type: [Array, Object],
      default: () => {},
    },
    creativeTemplateIds: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
    },
  },
  data() {
    return {
      isLoading: false,
      publishersList: [],
      minimumPublishers: 1,
      accessPublisherSelection: true,
      showIndividualPublisherSelection: false,
      publisherGroups: [],
      selectedPublisherGroups: [],
      allFetchedPublishers: {},
      publisherDeals: {},
      creativeSizes: [],
      publishersCreatives: {},
    };
  },
  computed: {
    ...mapState('campaigns', [
      'order',
      'publisherIds',
      'campaign',
    ]),
    ...mapGetters('campaigns', {
      isCampaignReadonly: types.GET_IS_CAMPAIGN_READONLY,
      isManagedCampaign: types.GET_IS_MANAGED_CAMPAIGN,
    }),
    ...mapState('templates', [
      'selectedTemplates',
      'availableTemplates',
      'isCreativeTemplateGroupChanged',
    ]),
    ...mapState('content', [
      'creatives',
    ]),
    totalPublishers() {
      if (this.publishersList.length === 0) return '';
      return this.publishersList.length;
    },
    totalselectedPublishers() {
      return this.publisherIds.length;
    },
    reachedMinimumPublishers() {
      return this.publisherIds.length === this.minimumPublishers;
    },
    publishersListAlphabetically() {
      return helper.orderBy(this.publishersList, 'name');
    },
    orderPublishers() {
      return this.orderSet.publisherIds;
    },
    publisherGroupName() {
      return this.isManagedCampaign ? 'MANAGED' : 'SELF_SERVICE';
    },
    publisherGroupsSectorName() {
      return this.isManagedCampaign ? 'MANAGED_SECTOR' : 'SECTOR';
    },
    getActiveCreatives() {
      return this.creatives.filter((creative) => !creative.paused && this.selectedTemplates.includes(creative.creativeTemplateId));
    },
    getActiveCreativeTemplates() {
      if (this.creatives.length && !this.isCreativeTemplateGroupChanged) return this.getActiveCreatives;
      const activeCreativeTemplates = [];
      this.selectedTemplates
        .forEach((templateId) => {
          const activeTemplate = this.availableTemplates.filter((aTemplate) => aTemplate.id === templateId);
          activeCreativeTemplates.push(...activeTemplate);
        });
      return activeCreativeTemplates;
    },
  },
  methods: {
    ...mapActions('campaigns', [
      types.FETCH_PUBLISHERS,
      types.FETCH_PUBLISHERS_GROUPS,
      types.PATCH_ORDER,
      types.ADD_PUBLISHER,
      types.DELETE_PUBLISHER,
      types.UPDATE_PUBLISHER_GROUP,
      types.FETCH_PUBLISHER_DEALS,
      types.FETCH_CREATIVE_SIZES,
      types.GET_PUBLISHERS_CREATIVES,
    ]),
    getPublishersCreatives() {
      const { advertiserId, id } = this.campaign;
      if (!id) return;
      this.GET_PUBLISHERS_CREATIVES({ advertiserId, campaignId: id })
        .then((pCreatives) => {
          this.publishersCreatives = pCreatives;
        });
    },
    getPublishersCreativesSliderValue(publisherId, creativeId) {
      const publisherCreatives = this.publishersCreatives[publisherId];
      if (!publisherCreatives) {
        return this.publisherIds.includes(publisherId);
      }
      const creative = publisherCreatives.find((aCreative) => aCreative.creativeId === creativeId);
      return !creative?.isPaused;
    },
    updatePublishersCreativesState(publisherId, creativeId, isPaused) {
      const defaultPublisherCreative = this.publishersCreatives[publisherId]?.filter((aCreative) => aCreative.creativeId === creativeId);
      if (this.publisherIds.includes(publisherId)) {
        this.$emit('updatePublishersCreativesState', {
          publisherId,
          creativeId,
          isPaused: !isPaused,
        }, defaultPublisherCreative);
      }
    },
    removePublisherFromPublishersCreativesList(publisherId) {
      this.$emit('removePublisherFromPublishersCreativesList', publisherId);
    },
    updatePublishersList(publishersList) {
      this.publishersList = publishersList
        .filter((publisher) => publisher?.id)
        .map((publisher) => {
          publisher.status = this.publisherIds.includes(publisher.id);
          return publisher;
        });
    },
    addAllPublisherIds() {
      this.orderPublishers.forEach((id) => {
        this.ADD_PUBLISHER(id);
      });
      this.updatePublishersList(this.publishersList);
    },
    togglePublisherStatus(id) {
      const publisherIndex = this.publisherIds.indexOf(id);
      if (publisherIndex === -1) {
        this.ADD_PUBLISHER(id);
      } else {
        this.DELETE_PUBLISHER(id);
        this.removePublisherFromPublishersCreativesList(id);
      }
      this.updatePublishersList(this.publishersList);
    },
    changePublisherStatus(id) {
      this.selectedPublisherGroups = [];
      this.UPDATE_PUBLISHER_GROUP([]);
      this.togglePublisherStatus(id);
    },
    updateSelectedGroups(list) {
      this.selectedPublisherGroups = list;
      this.UPDATE_PUBLISHER_GROUP(list);
    },
    publisherHasDeals(id) {
      return this.publisherDeals[id] && this.publisherDeals[id].deals.length > 0;
    },
    getCreativeSize(id) {
      const creative = this.creativeSizes.find((item) => item.id === id);
      return `${creative.width}x${creative.height}`;
    },
    getPublisherDealType(id) {
      let type = '';
      const publisher = this.publisherDeals[id];
      if (publisher && publisher.deals.length > 0) {
        const dealType = publisher.deals[0].type;
        switch (dealType) {
          case 'AUCTION':
            type = 'Open Auction';
            break;
          case 'PREFERRED':
            type = 'Preferred';
            break;
          default:
            type = '';
        }
      }
      return type;
    },
  },
  async created() {
    this.isLoading = true;
    this.accessPublisherSelection = feature.isVisible('INDIVIDUAL_PUBLISHER_SELECTION') && !this.isCampaignReadonly;
    const fetchPublishers = await this.FETCH_PUBLISHERS();
    const fetchPublishersGroupsSelfServicelist = await
    this.FETCH_PUBLISHERS_GROUPS(this.publisherGroupName);
    const fetchPublishersGroups = await
    this.FETCH_PUBLISHERS_GROUPS(this.publisherGroupsSectorName);
    Promise.all([fetchPublishers, fetchPublishersGroups, fetchPublishersGroupsSelfServicelist])
      .then(() => {
        const selfServicelist = fetchPublishersGroupsSelfServicelist[0].publisherIds
          .map((publisherId) => fetchPublishers
            .find((publisher) => publisher.id === publisherId));
        if ((Object.keys(this.orderSet).length !== 0)) {
          if (this.orderSet.publisherGroupIds.length !== 0) {
            this.selectedPublisherGroups = fetchPublishersGroups
              .filter((group) => this.orderSet.publisherGroupIds.includes(group.id))
              .map((item) => item.name);
            this.showIndividualPublisherSelection = false;
          } else if (this.orderSet.publisherIds.length !== 0) {
            this.updatePublishersList(selfServicelist);
            this.showIndividualPublisherSelection = true;
            this.addAllPublisherIds();
            this.isLoading = false;
          }
        } else {
          this.publisherGroups = fetchPublishersGroups;
          this.allFetchedPublishers = selfServicelist;
          this.updatePublishersList(selfServicelist);
          this.isLoading = false;
        }
        this.publisherGroups = fetchPublishersGroups;
        this.allFetchedPublishers = selfServicelist;
        this.updatePublishersList(selfServicelist);
        this.isLoading = false;
      });
    if (this.isManagedCampaign) {
      await this.FETCH_CREATIVE_SIZES().then((sizes) => { this.creativeSizes = sizes; });
      this.FETCH_PUBLISHER_DEALS().then((deals) => {
        const newData = deals.reduce((accu, { publisherId, ...rest }) => {
          if (!accu[publisherId]) accu[publisherId] = { deals: [rest] };
          else accu[publisherId] = { deals: [...accu[publisherId].deals, rest] };
          return accu;
        }, {});
        this.publisherDeals = newData;
      });
      this.getPublishersCreatives();
    }
  },
  watch: {
    orderPublishers(newVal, old) {
      if (newVal !== old) this.updatePublishersList(this.publishersList);
    },
    showIndividualPublisherSelection(newVal) {
      if (newVal) this.updatePublishersList(this.publishersList);
    },
  },
};
</script>
<style scoped lang="scss">
.custom-pub-c-slider{
  .custom-switch{
    display: unset;
  }
}
</style>
